import type { Photo } from '@/content/sections'
import type { Draftable } from '@/content/drafts'

/**
 * What a report IS, and the five helpers that build one.
 *
 * Extracted from `content/reports.ts` on 2026-08-30, for the same reason
 * `storyBuilder.ts` was extracted from `stories.ts`: the thirty-one report
 * entries now live in `content/reports/*.ts` and are pulled in by an EAGER
 * glob, which evaluates those files while `reports.ts` is still part-way
 * through its own initialisation. A globbed file importing `focus` from
 * `reports.ts` would therefore read an uninitialised binding — a
 * `ReferenceError` at prerender, after typecheck has passed. Importing from
 * here, which imports nothing back, cannot do that.
 *
 * So this module never imports `reports.ts`. If it ever needs to, the entries
 * have outgrown the arrangement and the fix is to move whatever it needs down
 * here, not to add the import.
 */


/**
 * The Impact Labs reports, and the page each one opens.
 *
 * The WordPress import brought `/impact-labs/reports/` across as **one paragraph
 * containing thirty-one anchors and nothing else** — so `LegacyPage` laid it out
 * as a flat list of names, and every one of those names rendered as plain grey
 * text rather than as a link, because `importedHref` drops an anchor whose target
 * was never imported. The reports page was therefore a wall of unclickable
 * titles: the single worst state for the page whose entire job is to hand a
 * reader a report.
 *
 * This module is the fix, asked for on 2026-08-26 with a screenshot of the live
 * grid and eight cover files. It is deliberately a **typed catalogue rather than
 * thirty-one imported JSON files**: the report pages are not a record of what
 * digitopia.co's markup said, they are a designed template filled per report, and
 * the block model in `content/blocks.ts` has no answer for a two-column hero with
 * a cover and a download button. `blocks.ts` says as much at the top — "pages
 * that matter enough to design properly graduate out of this".
 *
 * Two things here were honest gaps rather than oversights, and both ride on
 * `check` so a production build cannot quietly publish them (see `Check` in
 * `content/sections.ts`). The download gap is closed now — as is the cover one —
 * so the copy gap is all the amber notes are still about:
 *
 *  * **All thirty-one now have a download destination — the gap is closed.** On
 *    digitopia.co the button opens a HubSpot form whose id was not supplied, and a
 *    button that goes nowhere is worse than no button — so `download` stays
 *    optional and a report without one renders no CTA at all. Filling it in was one
 *    line per report: marketing supplied bucket URLs six times on 2026-08-27, every
 *    time as *"bu linkleri ilgili reportlara download olarak koy"* — first the
 *    Banking and Consumer Goods Future 2030 outlooks, then the Energy & Utilities,
 *    Manufacturing, Automotive and Retail outlooks together with the Türkiye
 *    edition of the maturity report, then the Technology, Tourism and
 *    Utilities Industry Focus cuts with the Insurance, Logistics and Pharmaceutical
 *    outlooks, then six more Industry Focus cuts — Logistics, Manufacturing,
 *    Pharmaceuticals, Public Services, Retail and Services — then six more again:
 *    Automotive, Banking, Construction, Consumer Goods, Energy and Insurance — and
 *    finally the last six, which were the six the note used to list as missing: the
 *    global and USA editions of the maturity report, Top 10 Trends, the Airports and
 *    Airlines cuts and the Services outlook. `download` is still optional because a
 *    thirty-second report will arrive before its file does, not because any current
 *    one lacks a link. The third batch is also what gave `focus()` a `doc` argument
 *    at all: until then no Industry Focus cut had a file, so only the outlooks
 *    could carry one.
 *
 *    **Two of the last six keys fold the title's `&` to `_`** — the file behind
 *    *Digital & AI Maturity Report 2026* is held as "Digitopia Digital _ AI Maturity
 *    Report 2026.pdf". Same ASCII fold that made the Türkiye edition's key
 *    "Turkiye…", so the same rule applies: the key is what the bucket holds, and
 *    tidying it to match the title gives a 404.
 *
 *    Eight sectors now have a file under **both** series — Automotive, Banking,
 *    Consumer Goods, Insurance, Logistics, Manufacturing, Pharmaceuticals and
 *    Retail — and the two are different
 *    documents: the object key says which. `digitopia-daimi-2026-report-retail.pdf`
 *    is the Industry Focus cut of the 2026 maturity report; a key naming an
 *    "outlook" is the Future 2030 one. Reading the sector out of the filename and
 *    stopping there would hang the maturity cut off the outlook page. Energy is
 *    the same trap wearing a different hat: `…-report-energy.pdf` is the Energy
 *    Industry Focus cut, not the Energy **& Utilities** outlook, and not the
 *    separate Utilities focus cut either.
 *  * **Every report now has a cover; most still have no summary of their own.**
 *    All thirty-one covers were supplied on 2026-08-26, in four batches: eight,
 *    then the ten remaining Industry Focus cuts bar Tourism and Utilities, then
 *    those two plus Top 10 Trends and seven of the ten Future 2030 outlooks, and
 *    finally the last three outlooks — Consumer Goods, Manufacturing and
 *    Automotive. Nothing falls back to the type tile any more. The reports whose
 *    own copy has since been read off a screenshot of their live page each hold it
 *    in their own module — Airlines and, from 2026-08-27, Automotive; everything
 *    else carries the series description, which is true of the series, and says so
 *    in amber.
 */
export interface Report extends Draftable {
  /** Path under `impact-labs/reports/` — the same slug digitopia.co uses, so the old link keeps working. */
  slug: string
  /** The title the index lists it under. */
  title: string
  /** The series line above the headline. */
  eyebrow: string
  /** The report page's own display headline. */
  headline: string
  /**
   * How the closing global-perspective band names this report in its first
   * sentence — "The Airports Focus Report is part of a larger, global study".
   * Absent falls back to the full title, which is right for a report whose title
   * is already short. See `content/reportClosing.ts`.
   */
  shortName?: string
  /**
   * Whether this report is a cut of the 2026 Digital & AI Maturity study.
   *
   * True of the seventeen Industry Focus cuts and of the flagship editions; false
   * of the Future 2030 outlooks and Top 10 Trends, which are their own series.
   * The closing band says "part of a larger, global study" either way, because
   * the request was for the identical closing on every report page — so this is
   * what raises the amber note where that sentence is a claim rather than a fact.
   */
  partOfStudy?: boolean
  /** The square cover artwork, where we hold it. */
  cover?: Photo
  /** What this report is. The report's own words where they were supplied. */
  body: string[]
  /** Where the download goes. Set for all thirty-one; optional for the next one to arrive. */
  download?: string
  /**
   * Which page draws this report. Absent — twenty-two of the thirty-one —
   * means the shared template in `pages/Reports.tsx`.
   *
   * `ReportPage` used to branch on the slug, because there was exactly one
   * designed page and a boolean nothing else could set is a worse way of saying
   * "this one is special". The note there said to make it a field when a second
   * one arrived; the Consumer Goods Industry Focus page arrived on 2026-08-27,
   * so this is that field — and the USA Edition arrived the same day, which is
   * what it cost to add a third: one value here and one branch there. The
   * Airports Industry Focus cut is the fourth, and cost the same again. The
   * Türkiye edition is the fifth, and cost the same a third time. The Airlines
   * Industry Focus cut is the sixth — asked for on 2026-08-27 with a full-page
   * screenshot and, for the first time on a focus cut, both of the page's
   * figures as files.
   *
   * `automotive-outlook` is the tenth and the first that is **not** a cut of the
   * maturity study: a Future 2030 outlook has three named requirements, five
   * predictions and a pointer at the maturity report, so it is a different set of
   * bands rather than the focus shape with a different sector in it.
   * `manufacturing-outlook` is the eleventh, asked for hours after it from the
   * same kind of capture — the same six bands with three of them shaped
   * differently, which is why it is a second value here rather than the tenth one
   * reused.
   */
  layout?:
    | 'daimi'
    | 'consumer-goods-focus'
    | 'usa-edition'
    | 'airports-focus'
    | 'turkiye-edition'
    | 'airlines-focus'
    | 'automotive-focus'
    | 'banking-focus'
    | 'construction-focus'
    | 'utilities-focus'
    | 'tourism-focus'
    | 'technology-focus'
    | 'services-focus'
    | 'retail-focus'
    | 'public-services-focus'
    // The Industry Focus cut for Manufacturing. Distinct from
    // `'manufacturing-outlook'` below — same sector, two reports, two pages.
    | 'manufacturing-focus'
    // The Industry Focus cut for Insurance. Distinct from `'insurance-outlook'`
    // below — same sector, two reports, two pages.
    | 'insurance-focus'
    // The Industry Focus cut for Energy. Distinct from `'energy-outlook'` below,
    // which covers Energy **& Utilities** as one sector, and from
    // `'utilities-focus'` above, which is a third report — three pages, not two.
    | 'energy-focus'
    // The Industry Focus cut for Logistics. Distinct from `'logistics-outlook'`
    // below — same sector, two reports, two pages.
    | 'logistics-focus'
    // The Industry Focus cut for Pharmaceuticals. Distinct from
    // `'pharmaceutical-outlook'` below — same sector, two reports, two pages — and
    // the value is plural because the slug and the sector name are.
    | 'pharmaceuticals-focus'
    | 'automotive-outlook'
    | 'manufacturing-outlook'
    | 'consumer-goods-outlook'
    | 'retail-outlook'
    | 'pharmaceutical-outlook'
    | 'banking-outlook'
    | 'services-outlook'
    | 'insurance-outlook'
    | 'logistics-outlook'
    | 'energy-outlook'
    | 'top-10-trends'
  /** An unresolved gap, rendered amber in a preview and gating production. */
  check?: string
}

export const DAIMI = 'Digital & AI Maturity Report 2026'
export const FOCUS_EYEBROW = `${DAIMI} / Industry Focus`
export const OUTLOOK_EYEBROW = 'Future 2030 / Digital Transformation Outlook'
export const LABS_EYEBROW = 'Digitopia Impact Labs'

/**
 * The series' own closing line, taken verbatim from the live Airlines page. It
 * describes the Industry Focus set rather than any one sector, which is why it
 * can stand under every report in the set.
 */
export const FOCUS_CLOSE =
  'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.'

/**
 * What is still missing for a report, said plainly and in one place.
 *
 * Returns `undefined` when nothing is — a report with a file, a cover and its own
 * words has no gap to annotate, and an empty "Still to be supplied:" line would be
 * an amber note about nothing that also blocks the production build.
 */
export function stillToCome(missing: {
  copy?: boolean
  cover?: boolean
  download?: boolean
}): string | undefined {
  const parts: string[] = []
  if (missing.download) {
    parts.push(
      'a download destination, so the Download Report button is not rendered on this page',
    )
  }
  if (missing.copy) {
    parts.push(
      'this report’s own summary from digitopia.co — the paragraphs above are the series description, not the sector’s',
    )
  }
  if (missing.cover) parts.push('the cover artwork')
  if (parts.length === 0) return undefined
  return `Still to be supplied for this report: ${parts.join('; ')}.`
}

/**
 * Where the PDFs live: the `website-documents` bucket, not `public/`.
 *
 * Same bucket and the same reasoning as the e-books' (`content/ebooks.ts`) — they
 * are large documents that change on marketing's schedule, and putting them in the
 * repository would put every future revision in the git history forever. Only the
 * folder differs: `report` rather than `ebook`.
 */
const BUCKET
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/report'

/**
 * One PDF's URL, from the object key exactly as the bucket holds it.
 *
 * The keys carry spaces — "6edec37a-…-Digital Transformation Outlook - Banking
 * Industry.pdf" — so the encoding happens here, once, rather than being
 * hand-written into the literals where a single missed `%20` is invisible until
 * somebody clicks.
 */
export function pdf(objectKey: string): string {
  return `${BUCKET}/${objectKey.replace(/ /g, '%20')}`
}

export function cover(name: string, title: string): Photo {
  return { src: `/photo/${name}.webp`, alt: `${title} cover` }
}

/**
 * One of the seventeen Industry Focus cuts of the 2026 report.
 *
 * `doc` is the object key in the reports folder of the bucket. All seventeen cuts
 * have one now; it stays optional so a cut can be listed before its PDF lands,
 * and a cut without one renders no button — same rule as `outlook()`.
 */
export function focus(slug: string, industry: string, art?: string, doc?: string): Report {
  const title = `${DAIMI} Industry Focus – ${industry}`
  return {
    slug,
    title,
    eyebrow: FOCUS_EYEBROW,
    headline: `Digital & AI Maturity Index in ${industry}`,
    shortName: `${industry} Focus Report`,
    partOfStudy: true,
    cover: art ? cover(art, title) : undefined,
    body: [
      `The ${industry} Industry Focus reads the sector against the same Digital & AI Maturity Index assessment as the global 2026 report, using the responses of the companies in it.`,
      FOCUS_CLOSE,
    ],
    download: doc ? pdf(doc) : undefined,
    check: stillToCome({ copy: true, cover: !art, download: !doc }),
  }
}

/**
 * One of the ten Future 2030 outlooks.
 *
 * `doc` is the object key in the reports folder of the bucket. All ten outlooks
 * have one now; it stays optional on the same terms as `focus()`, and an outlook
 * without one renders no button.
 */
export function outlook(
  slug: string,
  industry: string,
  title?: string,
  art?: string,
  doc?: string,
): Report {
  const name = title ?? `Future 2030: Digital Transformation Outlook for the ${industry} Industry`
  return {
    slug,
    title: name,
    eyebrow: OUTLOOK_EYEBROW,
    headline: `Digital Transformation Outlook for ${industry}`,
    shortName: `Future 2030 ${industry} Outlook`,
    partOfStudy: false,
    cover: art ? cover(art, name) : undefined,
    body: [
      `Future 2030 is Digitopia’s digital transformation outlook for the ${industry} industry — where the sector is heading over the decade, and what has to change on the way there.`,
      'It sets out the forces reshaping the sector and the capabilities companies need to build to meet them.',
    ],
    download: doc ? pdf(doc) : undefined,
    check: stillToCome({ copy: true, cover: !art, download: !doc }),
  }
}
